/**
 * Submit a cache-purge job. Enter one path per line; each non-empty, trimmed line
 * becomes an entry in the purge request. Invalidates the edge cache for those
 * paths on the selected domain.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Textarea,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateWafCachePurge, wafExtractError } from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';

/** Split a textarea value into trimmed, non-empty path lines. */
function parsePaths(raw: string): string[] {
  return raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

export function CachePurgeDialog({
  serviceId,
  domain,
  open,
  onOpenChange,
}: {
  serviceId: string;
  domain: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const [raw, setRaw] = useState('');

  useEffect(() => {
    if (open) setRaw('');
  }, [open]);

  const purge = useCreateWafCachePurge(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Purge submitted' }); trackFeatureAction('waf', 'cache', 'create'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Purge failed') }),
  });

  const paths = parsePaths(raw);
  const valid = paths.length > 0;
  const pending = purge.isPending;

  const submit = () => {
    if (!valid) return;
    purge.mutate({ domain, paths });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Purge cache</DialogTitle>
          <DialogDescription>
            Invalidate the edge cache for <strong>{domain}</strong>. Enter one path per line, e.g. <code>/index.html</code> or <code>/assets/*</code>.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Textarea
            rows={6}
            placeholder={'/index.html\n/css/*\n/api/v1/*'}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            disabled={pending}
            style={{ fontFamily: 'var(--vn-font-mono)' }}
          />
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
            {paths.length} path{paths.length === 1 ? '' : 's'} to purge.
          </span>
        </DialogBody>
        <DialogFooter>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !valid}>
            Submit purge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CachePurgeDialog;
